import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Milestone, MilestoneMessage } from '../types';
import CaseThread, { formatCaseDate, type CaseComposer } from './CaseThread';
import { CaseSteps, NextStepCard, type CaseStep, type ObjectionNextStep } from './ObjectionCase';

interface AttestationCaseProps {
  milestone: Milestone;
  userRole: 'company' | 'person';
  messages: MilestoneMessage[];
  nextStep: ObjectionNextStep;
  /** Replaces "Ditt nästa steg" above the card, e.g. "Innan du invänder". */
  nextStepKicker?: string;
  /** Extra content inside the next-step card, e.g. the support link. */
  nextStepExtra?: React.ReactNode;
  /** Notices that keep their own flows (ÄTA, escalation, hints). */
  notices?: React.ReactNode;
  onReferenceMessage?: (message: MilestoneMessage) => void;
  referenceBusy?: boolean;
  composer: CaseComposer;
  error?: string | null;
  /** Opened from a notification or e-mail: show the latest message (869fnf0e5). */
  revealLatestMessage?: boolean;
}

const formatAmount = (amount: number) => `${new Intl.NumberFormat('sv-SE').format(amount)} kr`;

/**
 * Ordinary attestation in the same design as the objection case: where the
 * stage stands, one next step, the money and the dialogue as a thread
 * (ClickUp 869fkhmbb).
 */
export default function AttestationCase({
  milestone,
  userRole,
  messages,
  nextStep,
  nextStepKicker,
  nextStepExtra,
  notices,
  onReferenceMessage,
  referenceBusy = false,
  composer,
  error,
  revealLatestMessage = false
}: AttestationCaseProps) {
  const { t } = useTranslation();
  const companyDone = milestone.companyAttestation === 'approved';
  const personDone = milestone.personAttestation === 'approved';
  const paid = Boolean(milestone.paidAt);
  const workDone = companyDone || ['ready_for_attestation', 'seller_attested', 'ready_for_reattestation'].includes(milestone.deliveryStatus ?? '');
  const isPriceAdjustment = milestone.itemType === 'variation_price_adjustment';
  const done = [workDone, companyDone, personDone, paid];
  const currentStep = done.indexOf(false) === -1 ? done.length : done.indexOf(false);
  const now = t('objectionCase.steps.now');
  const hint = (index: number, doneHint?: string | null, idleHint?: string) =>
    index === currentStep ? now : index < currentStep ? formatCaseDate(doneHint) : idleHint;
  const steps: CaseStep[] = [
    { label: t(isPriceAdjustment ? 'attestationCase.steps.readyVariation' : 'attestationCase.steps.ready'), hint: hint(0, milestone.submittedAt) },
    { label: t(userRole === 'company'
      ? companyDone ? 'attestationCase.steps.youAttested' : 'attestationCase.steps.yourAttest'
      : companyDone ? 'attestationCase.steps.companyAttested' : 'attestationCase.steps.companyAttest'), hint: hint(1, milestone.submittedAt) },
    { label: t(userRole === 'person' ? 'attestationCase.steps.yourAttest' : 'attestationCase.steps.personAttest'), hint: hint(2, milestone.jointlyAttestedAt) },
    { label: t('attestationCase.steps.payout'), hint: hint(3, milestone.paidAt,
      t(userRole === 'person' ? 'attestationCase.steps.payoutHintPerson' : 'attestationCase.steps.payoutHintCompany')) }
  ];
  const fullyApproved = companyDone && personDone;
  const amount = formatAmount(milestone.amount);

  return (
    <div className="objection-case" data-testid="attestation-case">
      <div className="objection-steps-block">
        <CaseSteps steps={steps} currentStep={currentStep} />
      </div>

      <NextStepCard nextStep={nextStep} kicker={nextStepKicker}>{nextStepExtra}</NextStepCard>

      {notices}

      {/* Price changes of zero or a credit pay nothing out. */}
      {milestone.amount > 0 && (
        <div className="objection-money" data-testid="attestation-money">
          <i className={paid ? 'feather-check-circle' : 'feather-lock'} aria-hidden="true"></i>
          <div>
            <div className="fw-semibold">
              {t(paid ? 'attestationCase.money.paidTitle' : 'attestationCase.money.title', { amount })}
            </div>
            {!paid && (
              <div className="small">
                {t(fullyApproved
                  ? 'attestationCase.money.approved'
                  : userRole === 'person' ? 'attestationCase.money.person' : 'attestationCase.money.company')}
              </div>
            )}
          </div>
        </div>
      )}

      <CaseThread messages={messages} userRole={userRole} composer={composer}
        onReferenceMessage={onReferenceMessage} referenceBusy={referenceBusy} error={error}
        revealLatest={revealLatestMessage} />
    </div>
  );
}
